import React from 'react';
import { Box, Typography } from '@mui/material';
import { ConnectionLine } from './GuitarNeckVisualizerComponents';
import TouchableNoteMarker from './TouchableNoteMarker';
import '../styles/NoteHighlighting.css';
import { 
  getNoteAtFret, 
  isModeColorNote, 
  getReferenceNoteForModeColorNote,
  findFretForNote,
  SINGLE_DOT_FRETS,
  DOUBLE_DOT_FRETS
} from './GuitarNeckVisualizer.utils';
import { getProperNoteDisplay, resolveAllNotesIndex, getIntervalDegreeLabel } from '../utils/musicTheory';

const EMPTY_PITCH_SET = new Set();

// The green anchor. It follows activeScale.anchorPc, which tracks a chord
// degree (see useScaleLogic); on degree 1 that is the scale root, so this
// keeps its original meaning until the drill moves the anchor.
// Compare by pitch class: scaleRoot is spelled internally with sharps
// (e.g. "F#") while the fret note may be display-spelled with flats
// (e.g. "Gb"). A string compare would miss flat-spelled roots.
const isRootNoteAt = (stringName, fret, activeScale) => {
  if (!activeScale) return false;
  const anchorPc = Number.isInteger(activeScale.anchorPc)
    ? activeScale.anchorPc
    : resolveAllNotesIndex(activeScale.scaleRoot);
  return resolveAllNotesIndex(getNoteAtFret(stringName, fret)) === anchorPc;
};

// The scale root once the anchor has moved off it: outlined, not filled, so
// chasing the 3rd or the 7th does not cost the tonal bearing.
const isTonicOutlineAt = (stringName, fret, activeScale) => {
  if (!activeScale || !Number.isInteger(activeScale.tonicPc)) return false;
  if (activeScale.anchorPc === activeScale.tonicPc) return false;
  return resolveAllNotesIndex(getNoteAtFret(stringName, fret)) === activeScale.tonicPc;
};

const isNoteInScaleAt = (stringName, fret, activeScale) => {
  if (!activeScale) return false;
  const note = getNoteAtFret(stringName, fret);
  // Use the proper display note to check if it's in the scale
  const displayNote = getProperNoteDisplay(note, activeScale);
  return activeScale.notes.includes(displayNote);
};

// Ghost notes: parent-scale notes a pentatonic/blues scale skips (e.g. F and B
// in C Major Pentatonic). Shown dimmed so the borrowed diatonic chords make sense.
const getGhostPitchSet = (activeScale) => {
  const set = new Set();
  if (activeScale && Array.isArray(activeScale.ghostNotes)) {
    activeScale.ghostNotes.forEach(n => set.add(resolveAllNotesIndex(n)));
  }
  return set;
};

// Etats d'une case du manche, partages entre les frettes 1 a 24 de StringRow et
// la case frette 0 rendue par le nom de corde dans GuitarNeckVisualizer.
export const getFretCellState = ({
  stringName,
  stringIndex,
  fret,
  fretIndex = 0,
  activeScale,
  ghostPitchSet = EMPTY_PITCH_SET,
  isNoteInPattern,
  isArpeggioNote,
  selectedArpeggio = null,
  isChordNote,
  highlightedFrets = []
}) => {
  const isInScale = isNoteInScaleAt(stringName, fret, activeScale);
  const noteValue = getNoteAtFret(stringName, fret);
  const displayNote = getProperNoteDisplay(noteValue, activeScale);
  return {
    fretIndex,
    actualFret: fret,
    noteValue,
    isInScale,
    isGhost: !isInScale && ghostPitchSet.size > 0 && ghostPitchSet.has(resolveAllNotesIndex(noteValue)),
    ghostLabel: displayNote,
    isRoot: isRootNoteAt(stringName, fret, activeScale),
    isTonicOutline: isTonicOutlineAt(stringName, fret, activeScale),
    noteIsInPattern: typeof isNoteInPattern === 'function' && isNoteInPattern(stringIndex, fret),
    isArpeggio: typeof isArpeggioNote === 'function' && isArpeggioNote(stringIndex, fret, activeScale, selectedArpeggio),
    isChordHighlightNote: typeof isChordNote === 'function' && isChordNote(stringIndex, fret, activeScale),
    isChordTone: !!(
      activeScale &&
      Array.isArray(activeScale.chordTones) &&
      activeScale.chordTones.includes(displayNote)
    ),
    isMarkerFret: SINGLE_DOT_FRETS.includes(fret) || DOUBLE_DOT_FRETS.includes(fret),
    isHighlightedFret: highlightedFrets.includes(fret)
  };
};

// Une case porte un marqueur si sa note est dans la gamme ; pendant une lecon,
// seulement si elle est dans la section ou dans le pas courant.
export const hasFretNoteMarker = (cell, lessonMode, lessonNote) => (
  lessonMode ? !!lessonNote : cell.isInScale
);

// Le marqueur d'une case, avec les neutralisations du paint mode et de la lecon.
// Rendu par StringRow pour chaque frette et par le nom de corde pour la frette 0.
export const FretNoteMarker = ({
  cell,
  lessonMode = false,
  lessonNote = null,
  compact = false,
  noteOpacity = 1,
  isEarLit = false,
  stringIndex,
  activeScale,
  selectedNotes,
  highlightArpeggios,
  selectedArpeggio,
  getArpeggioDegree,
  isPaintMode = false,
  highlightChordNotes,
  getChordDegree,
  currentColor,
  visibleColors,
  handleNoteClick,
  handleNoteInteraction,
  noteDisplayMode = 'none',
  fretMarkerDots = false,
  showWholeScale = false,
  coachPulse = false
}) => {
  if (!hasFretNoteMarker(cell, lessonMode, lessonNote)) return null;
  const { actualFret, noteValue, isInScale, isRoot, isTonicOutline, noteIsInPattern, isArpeggio, isChordHighlightNote, isChordTone } = cell;
  // Les frettes reperees d'une guitare, reportees dans le rond : un point sur
  // 3, 5, 7, 9..., deux sur 12 et 24. Le compte se decide ici, la ou les
  // constantes vivent deja ; le marqueur ne recoit qu'un nombre.
  const markerDots = fretMarkerDots
    ? (DOUBLE_DOT_FRETS.includes(actualFret) ? 2 : (SINGLE_DOT_FRETS.includes(actualFret) ? 1 : 0))
    : 0;
  return (
    <TouchableNoteMarker
      stringIndex={stringIndex}
      actualFret={actualFret}
      noteValue={noteValue}
      activeScale={activeScale}
      selectedNotes={selectedNotes}
      highlightArpeggios={(isPaintMode || lessonMode) ? false : highlightArpeggios}
      selectedArpeggio={isPaintMode ? null : selectedArpeggio}
      getArpeggioDegree={getArpeggioDegree}
      isRootNote={isPaintMode ? false : isRoot}
      isTonicOutline={isPaintMode ? false : isTonicOutline}
      isModeColor={false} // Temporarily disabled - mode color notes show as regular scale notes
      noteIsInPattern={isPaintMode ? false : noteIsInPattern}
      isInScale={isInScale}
      isArpeggio={isPaintMode ? false : isArpeggio}
      isChordTone={isPaintMode ? false : isChordTone}
      highlightChordNotes={(isPaintMode || lessonMode) ? false : highlightChordNotes}
      isChordHighlightNote={isPaintMode ? false : isChordHighlightNote}
      getChordDegree={getChordDegree}
      currentColor={currentColor}
      visibleColors={visibleColors}
      handleNoteClick={handleNoteClick}
      handleNoteInteraction={handleNoteInteraction}
      isPaintMode={isPaintMode}
      noteOpacity={noteOpacity}
      noteDisplayMode={noteDisplayMode}
      markerDots={markerDots}
      showWholeScale={showWholeScale}
      isEarLit={isEarLit}
      coachPulse={coachPulse}
      lessonMode={lessonMode}
      lessonNote={lessonNote}
      compact={compact}
    />
  );
};

const StringRow = ({
  stringName,
  stringIndex,
  startFret,
  numFretsToShow,
  activeScale,
  selectedNotes,
  hasSelectedNote,
  highlightArpeggios,
  isNoteInPattern,
  isArpeggioNote,
  getArpeggioDegree,
  visibleStrings,
  toggleStringVisibility,
  handleNoteClick,
  handleNoteInteraction,
  isPaintMode = false,
  drawConnections,
  hideStringColumn = false,
  className = "",
  highlightedFrets = [],
  currentColor = 'orange',
  visibleColors = ['orange', 'red', 'green', 'blue'],
  selectedArpeggio = null,
  highlightChordNotes = false,
  isChordNote,
  getChordDegree,
  noteDisplayMode = 'none',
  fretMarkerDots = false,
  showWholeScale = false,
  earPosition = null,
  coachPulse = false,
  lessonNotes = null,
  leftHanded = false
}) => {
  // Une lecon est ouverte des que le manche recoit un tableau de notes de pas.
  const lessonMode = Array.isArray(lessonNotes);
  // Debug logging (disabled for now)
  // if (stringIndex === 0) {
  //     selectedNotes,
  //     selectedArpeggio, 
  //     highlightArpeggios 
  //   });
  // }
  // Fixed width for each fret - must match the width in FretboardHeader
  const FRET_WIDTH = 60; // 60px per fret

  const ghostPitchSet = React.useMemo(() => getGhostPitchSet(activeScale), [activeScale]);

  // Notes de lecon portees par cette corde (section + pas courant), indexees par
  // frette. Nulle hors lecon, donc le chemin par defaut reste exactement "en
  // gamme ou rien". Gardee hors du memo des cellules ci-dessous, qui ne doit pas
  // se recalculer a chaque pas d'une lecon.
  const lessonNotesOnString = React.useMemo(() => {
    if (!lessonMode) return null;
    const map = new Map();
    lessonNotes.forEach(note => {
      if (note && note.stringIndex === stringIndex) map.set(note.fret, note);
    });
    return map;
  }, [lessonMode, lessonNotes, stringIndex]);

  // Per-cell flags precomputed once per relevant change. Kept out of the JSX map
  // so renders driven by earPosition/coachPulse (350ms steps during "Hear the
  // scale") skip the ~10 music-theory calls per cell.
  const cells = React.useMemo(() => {
    return Array.from({ length: numFretsToShow + 1 }).map((_, fretIndex) => getFretCellState({
      stringName,
      stringIndex,
      fret: startFret + fretIndex,
      fretIndex,
      activeScale,
      ghostPitchSet,
      isNoteInPattern,
      isArpeggioNote,
      selectedArpeggio,
      isChordNote,
      highlightedFrets
    }));
  }, [stringName, stringIndex, startFret, numFretsToShow, activeScale, ghostPitchSet, isNoteInPattern, isArpeggioNote, selectedArpeggio, isChordNote, highlightedFrets]);

  return (
    <Box 
      className={`${className}`}
      sx={{ 
        display: 'flex',
        flexDirection: leftHanded ? 'row-reverse' : 'row',
        position: 'relative',
        height: 'var(--string-row-height, 36px)',
        width: '100%',
        minWidth: `${(numFretsToShow + 1) * FRET_WIDTH}px`, // Ensure minimum width matches fretboard
        backgroundColor: '#121212',
      }}
    >
      {/* String line */}
      <Box 
        className={`string-row-line`}
        sx={{
          position: 'absolute',
          left: leftHanded ? 'auto' : (hideStringColumn ? 0 : '30px'), // Start after the string label
          right: leftHanded ? (hideStringColumn ? 0 : '30px') : 'auto',
          width: `${(numFretsToShow + 1) * FRET_WIDTH}px`, // Set explicit width to match fretboard
          top: '50%',
          height: stringIndex < 2 ? '1.8px' : stringIndex < 4 ? '1.4px' : '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          transform: 'translateY(-50%)',
          zIndex: 1 // Lower z-index so notes can cover it
        }} 
      />
      
      {/* String label - clickable for toggling visibility - only show if not hidden */}
      {!hideStringColumn && (
        <Box 
          className={`string-row-name-cell`}
          sx={{ 
            width: '30px',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRight: leftHanded ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
            borderLeft: leftHanded ? '1px solid rgba(255, 255, 255, 0.15)' : 'none',
            borderBottom: '1px solid rgba(255, 255, 255, 0)', // Invisible border-bottom
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            position: 'relative',
            cursor: 'pointer',
          }}
          onClick={() => toggleStringVisibility(stringIndex)}
        >
          <Typography 
            className="string-row-name-label"
            variant="body2" 
            sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}
          >
            {stringName}
          </Typography>
        </Box>
      )}
      
      {/* Frets */}
      <Box 
        className="string-row-frets-container"
        sx={{ 
          display: 'flex', 
          flexDirection: leftHanded ? 'row-reverse' : 'row',
          position: 'relative',
          width: leftHanded ? '100%' : 'auto',
          minWidth: `${(numFretsToShow + 1) * FRET_WIDTH}px`, // +1 to account for the nut/open string
        }}
      >
        {/* Connection lines for mode color notes */}
        {drawConnections && Array.from({ length: numFretsToShow + 1 }).map((_, fretIndex) => {
          const actualFret = startFret + fretIndex;
          const note = getNoteAtFret(stringName, actualFret);

          // Convert note to the scale's preferred spelling before checking
          const properNote = getProperNoteDisplay(note, activeScale);

          // Only draw lines for mode color notes
          if (isModeColorNote(properNote, activeScale)) {
            const referenceNote = getReferenceNoteForModeColorNote(properNote, activeScale);
            const referenceFret = findFretForNote(stringIndex, referenceNote, startFret, numFretsToShow);
            
            // Only draw if the reference fret is visible and close enough to the altered note
            if (referenceFret >= startFret && 
                referenceFret <= startFret + numFretsToShow && 
                Math.abs(referenceFret - actualFret) <= 3) {
              
              // Calculate the center of each fret cell
              const fretCellWidth = 100 / (numFretsToShow + 1);
              
              // Position of the centers of the reference and current frets
              const refFretPos = (referenceFret - startFret) * fretCellWidth + (fretCellWidth / 2);
              const currFretPos = fretIndex * fretCellWidth + (fretCellWidth / 2);
              
              // Width is the distance between the centers
              const width = leftHanded
                ? `${Math.abs(referenceFret - actualFret) * FRET_WIDTH}px`
                : `${Math.abs(refFretPos - currFretPos)}%`;
              
              // Start from the leftmost center
              const left = leftHanded
                ? `calc(100% - ${(Math.max(referenceFret - startFret, fretIndex) * FRET_WIDTH) + (FRET_WIDTH / 2)}px)`
                : `calc(${Math.min(refFretPos, currFretPos)}% + 15px)`;
              
              return (
                <ConnectionLine
                  key={`conn-mode-${stringIndex}-${fretIndex}`}
                  className="string-row-connection-line"
                  sx={{
                    left,
                    width,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                />
              );
            }
          }
          return null;
        })}
        
        {cells.map((cell) => {
          const { fretIndex, actualFret, isInScale, isGhost, isMarkerFret, isHighlightedFret } = cell;

          // Pendant une lecon la reference n'est plus la gamme mais la section :
          // seules ses notes sont rendues, meme hors gamme, et tout le reste du
          // manche reste vide.
          const lessonNote = lessonNotesOnString ? (lessonNotesOnString.get(actualFret) || null) : null;

          // Calculate opacity based on visible strings
          const noteOpacity = visibleStrings === null || visibleStrings.has(stringIndex) ? 1 : 0.1;

          return (
            <Box
              key={fretIndex}
              className={`string-row-fret ${isMarkerFret ? 'string-row-fret-marker' : ''} ${isInScale ? 'string-row-fret-has-scale-note' : ''} ${isHighlightedFret ? 'highlighted-fret' : ''}`}
              sx={{
                width: `${FRET_WIDTH}px`, // Use the constant for consistency
                height: '100%',
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRight: leftHanded ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: leftHanded ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0)', // Invisible border-bottom
                backgroundColor: isHighlightedFret ? 'rgba(33, 150, 243, 0.3)' : 'transparent',
                boxSizing: 'border-box',
                flexShrink: 0, // Prevent frets from shrinking
                transition: 'background-color 0.2s ease'
              }}
            >
              {/* Note marker for notes in scale; during a lesson, only the notes of the section and of the current step */}
              <FretNoteMarker
                cell={cell}
                lessonMode={lessonMode}
                lessonNote={lessonNote}
                noteOpacity={noteOpacity}
                isEarLit={!!earPosition && earPosition.stringIndex === stringIndex && earPosition.fret === actualFret}
                stringIndex={stringIndex}
                activeScale={activeScale}
                selectedNotes={selectedNotes}
                highlightArpeggios={highlightArpeggios}
                selectedArpeggio={selectedArpeggio}
                getArpeggioDegree={getArpeggioDegree}
                isPaintMode={isPaintMode}
                highlightChordNotes={highlightChordNotes}
                getChordDegree={getChordDegree}
                currentColor={currentColor}
                visibleColors={visibleColors}
                handleNoteClick={handleNoteClick}
                handleNoteInteraction={handleNoteInteraction}
                noteDisplayMode={noteDisplayMode}
                fretMarkerDots={fretMarkerDots}
                showWholeScale={showWholeScale}
                coachPulse={coachPulse}
              />


              {/* Ghost marker: parent-scale note the pentatonic skips. Dimmed, non-interactive. */}
              {isGhost && !lessonMode && (
                <Box
                  className="string-row-ghost-note"
                  sx={{
                    width: 'var(--neck-note-size, 32px)',
                    height: 'var(--neck-note-size, 32px)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.4)',
                    backgroundColor: 'rgba(0, 0, 0, 0.55)',
                    opacity: visibleStrings === null || visibleStrings.has(stringIndex) ? 0.55 : 0.1,
                    pointerEvents: 'none',
                    zIndex: 2,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    boxSizing: 'border-box'
                  }}
                >
                  {noteDisplayMode === 'names' ? cell.ghostLabel : ''}
                  {noteDisplayMode === 'degrees' ? getIntervalDegreeLabel(cell.noteValue, activeScale) : ''}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default React.memo(StringRow);
